import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LightbulbIcon,
  BookOpenTextIcon,
  MapTrifoldIcon,
  UserIcon,
  ImagesIcon,
  PencilSimpleIcon,
  FilmStripIcon,
  CaretDownIcon,
  CheckIcon,
  FolderIcon,
  ArticleIcon,
  SpeakerHighIcon,
  ImageSquareIcon,
  type Icon,
} from '@phosphor-icons/react';
import { useProject } from '../context/ProjectContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

export type CharacterTab = 'biography' | 'voice' | 'reference' | 'images';

export const TAB_CONFIG: Record<CharacterTab, { icon: Icon; label: string }> = {
  biography: { icon: ArticleIcon, label: 'Biography' },
  voice: { icon: SpeakerHighIcon, label: 'Voice' },
  reference: { icon: ImagesIcon, label: 'Reference' },
  images: { icon: ImageSquareIcon, label: 'Images' },
};

export const CHARACTER_TABS: CharacterTab[] = ['biography', 'voice', 'reference', 'images'];

const PAGE_CONFIG = {
  idea: { icon: LightbulbIcon, label: 'Idea', route: '/idea' },
  story: { icon: BookOpenTextIcon, label: 'Story', route: '/story' },
  atlas: { icon: MapTrifoldIcon, label: 'Atlas', route: '/atlas' },
  character: { icon: UserIcon, label: 'Character', route: '/character' },
  lookbook: { icon: ImagesIcon, label: 'Lookbook', route: '/lookbook' },
  doodle: { icon: PencilSimpleIcon, label: 'Doodle', route: '/doodle' },
  storyboard: { icon: FilmStripIcon, label: 'Storyboard', route: '/storyboard' },
};

type PageType = keyof typeof PAGE_CONFIG;
const PROJECT_PAGES: PageType[] = ['idea', 'story', 'atlas', 'character', 'lookbook', 'doodle', 'storyboard'];

interface CharacterProductionBarProps {
  characterName: string;
  activeTab: CharacterTab;
  onTabChange: (tab: CharacterTab) => void;
  onBack?: () => void;
  children?: React.ReactNode; // Action buttons to show in right section
}

export const CharacterProductionBar: React.FC<CharacterProductionBarProps> = ({
  characterName,
  activeTab,
  onTabChange,
  onBack,
  children,
}) => {
  const navigate = useNavigate();
  const { selectedProjectId, setSelectedProjectId, projects, projectsLoading, joinedProjects, joinedProjectsLoading } = useProject();

  // Check both owned and joined projects
  const selectedProject = projects.find(p => p.id === selectedProjectId) ||
                          joinedProjects.find(p => p.id === selectedProjectId);

  const isLoading = projectsLoading || joinedProjectsLoading;
  const hasAnyProjects = projects.length > 0 || joinedProjects.length > 0;

  const handleProjectSelect = (projectId: string) => {
    setSelectedProjectId(projectId);
  };

  const handlePageSelect = (targetPage: PageType) => {
    if (targetPage !== 'character') {
      navigate(PAGE_CONFIG[targetPage].route);
    }
  };

  return (
    <div className="h-9 bg-onyx border-b border-white/5 flex items-center px-3 flex-shrink-0 relative">
      {/* Left Section: Project & Page Dropdowns */}
      <div className="flex items-center gap-2">
        {/* Project Selector */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-sm text-white/90 hover:text-white hover:bg-white/5 transition-colors flex items-center gap-1.5"
            >
              <FolderIcon className="w-3.5 h-3.5 text-white/50" />
              <span className="max-w-[140px] truncate">
                {isLoading ? 'Loading...' : selectedProject?.title || 'Select Project'}
              </span>
              <CaretDownIcon className="w-3 h-3 text-white/50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[240px] bg-carbon border-zinc-800 text-zinc-200">
            {!hasAnyProjects ? (
              <div className="px-2 py-3 text-sm text-zinc-500 text-center">No projects yet</div>
            ) : (
              <>
                {/* My Projects */}
                {projects.length > 0 && (
                  <>
                    <DropdownMenuLabel className="text-xs text-zinc-500 uppercase tracking-wider font-normal px-2 py-1.5">
                      My Projects
                    </DropdownMenuLabel>
                    {projects.map(project => (
                      <DropdownMenuItem
                        key={project.id}
                        onClick={() => handleProjectSelect(project.id)}
                        className="text-sm focus:bg-white/10 focus:text-white cursor-pointer flex justify-between group"
                      >
                        <span className="truncate">{project.title}</span>
                        {project.id === selectedProjectId && (
                          <CheckIcon className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                        )}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}

                {/* Joined Projects */}
                {joinedProjects.length > 0 && (
                  <>
                    {projects.length > 0 && <DropdownMenuSeparator className="bg-white/5" />}
                    <DropdownMenuLabel className="text-xs text-zinc-500 uppercase tracking-wider font-normal px-2 py-1.5">
                      Joined Projects
                    </DropdownMenuLabel>
                    {joinedProjects.map(project => (
                      <DropdownMenuItem
                        key={project.id}
                        onClick={() => handleProjectSelect(project.id)}
                        className="text-sm focus:bg-white/10 focus:text-white cursor-pointer flex justify-between group"
                      >
                        <span className="truncate">{project.title}</span>
                        {project.id === selectedProjectId && (
                          <CheckIcon className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                        )}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Divider */}
        <div className="h-4 w-px bg-white/10" />

        {/* Page Selector Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors flex items-center gap-1.5"
            >
              <UserIcon className="w-3.5 h-3.5 text-white/60" />
              <span className="font-medium">Character</span>
              <CaretDownIcon className="w-3 h-3 text-white/50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[200px] bg-carbon border-zinc-800 text-zinc-200">
            <DropdownMenuLabel className="text-xs text-zinc-500 uppercase tracking-wider font-normal px-2 py-1.5">
              Pages
            </DropdownMenuLabel>
            {PROJECT_PAGES.map(p => {
              const pConfig = PAGE_CONFIG[p];
              const PIcon = pConfig.icon;
              const isActive = p === 'character';
              return (
                <DropdownMenuItem
                  key={p}
                  onClick={() => handlePageSelect(p)}
                  className={`text-sm cursor-pointer flex items-center gap-2 ${isActive ? 'bg-white/5 text-white' : 'focus:bg-white/10 focus:text-white'}`}
                >
                  <PIcon className={`w-4 h-4 ${isActive ? 'text-indigo-400' : 'text-white/50'}`} />
                  <span className="flex-1">{pConfig.label}</span>
                  {isActive && (
                    <CheckIcon className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                  )}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Divider */}
        <div className="h-4 w-px bg-white/10" />

        {/* Character Name */}
        <span className="text-sm font-semibold text-white/90 max-w-[180px] truncate">
          {characterName || 'Unnamed'}
        </span>
      </div>

      {/* Right Section: Action buttons + Back button */}
      <div className="ml-auto flex items-center gap-2">
        {children}
        {onBack && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="h-7 px-3 text-xs text-white/60 hover:text-white hover:bg-white/5 transition-colors"
          >
            Back to Characters
          </Button>
        )}
      </div>
    </div>
  );
};
